import { useState } from 'react'

import { gauss, mixing, molecularSpeed, ru, solidHopRatio } from '../../lib/physics'
import { Choice, Predict, Slider, useFrames } from './common'

/**
 * Диффузия в газе, жидкости и твёрдом теле (урок 12): два вещества сами проникают друг
 * в друга. В газе молекулы летают далеко — перемешивание за минуты; в жидкости бродят,
 * толкаясь, — за часы; в твёрдом теле дрожат на местах и лишь изредка меняются местами —
 * за годы. Нагрев ускоряет всё.
 */

type Тело = 'газ' | 'жидкость' | 'твёрдое тело'

const W = 300
const H = 160
const КОЛ = 20
const РЯД = 10
const ШАГ = W / КОЛ

interface Частица {
  x: number
  y: number
  vx: number
  vy: number
  вид: 0 | 1
  место: number // номер узла решётки (для твёрдого тела)
}

function старт(тело: Тело): Частица[] {
  if (тело === 'газ') {
    return Array.from({ length: 60 }, (_, i) => {
      const вид = (i < 30 ? 0 : 1) as 0 | 1
      const угол = Math.random() * 2 * Math.PI
      return { x: (вид ? W / 2 : 0) + Math.random() * (W / 2), y: Math.random() * H, vx: Math.cos(угол), vy: Math.sin(угол), вид, место: 0 }
    })
  }
  return Array.from({ length: КОЛ * РЯД }, (_, i) => {
    const кол = i % КОЛ
    const вид = (кол < КОЛ / 2 ? 0 : 1) as 0 | 1
    return { x: (кол + 0.5) * ШАГ, y: (Math.floor(i / КОЛ) + 0.5) * (H / РЯД), vx: 0, vy: 0, вид, место: i }
  })
}

/** Сколько условного времени проходит за секунду опыта и как его назвать. */
const ВРЕМЯ: Record<Тело, { единица: string; за_секунду: number }> = {
  газ: { единица: 'мин', за_секунду: 0.5 },
  жидкость: { единица: 'ч', за_секунду: 0.5 },
  'твёрдое тело': { единица: 'лет', за_секунду: 0.25 },
}

export default function DiffusionLab() {
  const [тело, setТело] = useState<Тело>('газ')
  const [t, setT] = useState(20)
  const [s, setS] = useState(() => ({ ч: старт('газ'), время: 0 }))

  const скорость = molecularSpeed(t) / molecularSpeed(20) // во сколько раз молекулы быстрее, чем при 20 °C

  useFrames((dt) => {
    setS((p) => {
      const ч = p.ч.map((q) => ({ ...q }))
      if (тело === 'газ') {
        for (const q of ч) {
          // летит по прямой, изредка сталкивается и меняет направление
          if (Math.random() < 1.5 * dt * скорость) {
            const угол = Math.random() * 2 * Math.PI
            q.vx = Math.cos(угол)
            q.vy = Math.sin(угол)
          }
          q.x += q.vx * 90 * скорость * dt
          q.y += q.vy * 90 * скорость * dt
          if (q.x < 3 || q.x > W - 3) q.vx = -q.vx
          if (q.y < 3 || q.y > H - 3) q.vy = -q.vy
          q.x = Math.min(Math.max(q.x, 3), W - 3)
          q.y = Math.min(Math.max(q.y, 3), H - 3)
        }
      } else {
        // жидкость: соседи часто меняются местами; твёрдое тело — редко и резко быстрее при нагреве
        const частота = тело === 'жидкость' ? 6 * скорость : 0.02 * Math.min(solidHopRatio(t), 2000)
        const обменов = Math.round(частота * dt * ч.length * 0.3 + Math.random())
        const поМесту = new Map(ч.map((q) => [q.место, q]))
        for (let k = 0; k < обменов; k++) {
          const a = Math.floor(Math.random() * ч.length)
          const кол = a % КОЛ
          const b = Math.random() < 0.5 ? (кол < КОЛ - 1 ? a + 1 : a - 1) : a + КОЛ < ч.length ? a + КОЛ : a - КОЛ
          const qa = поМесту.get(a)
          const qb = поМесту.get(b)
          if (qa && qb) {
            qa.место = b
            qb.место = a
            поМесту.set(a, qb)
            поМесту.set(b, qa)
          }
        }
        const дрожь = (тело === 'жидкость' ? 2.2 : 1.2) * Math.sqrt(скорость)
        for (const q of ч) {
          const хЦель = ((q.место % КОЛ) + 0.5) * ШАГ
          const уЦель = (Math.floor(q.место / КОЛ) + 0.5) * (H / РЯД)
          q.x += (хЦель - q.x) * Math.min(1, 10 * dt) + gauss() * дрожь * 0.3
          q.y += (уЦель - q.y) * Math.min(1, 10 * dt) + gauss() * дрожь * 0.3
        }
      }
      return { ч, время: p.время + dt * ВРЕМЯ[тело].за_секунду }
    })
  })

  const сменить = (v: Тело) => {
    setТело(v)
    setS({ ч: старт(v), время: 0 })
  }
  const м = mixing(
    s.ч.map((q) => q.x),
    s.ч.map((q) => q.вид),
    W,
  )
  const r = тело === 'газ' ? 4 : 6.5

  return (
    <div className="card">
      <svg className="lab-svg" viewBox={`-6 -6 ${W + 12} ${H + 12}`} role="img" aria-label="Два вещества перемешиваются">
        <rect x={-3} y={-3} width={W + 6} height={H + 6} rx={4} className="fill-paper ink" strokeWidth={1.5} />
        <line x1={W / 2} y1={-3} x2={W / 2} y2={H + 3} className="ink-muted" strokeDasharray="3 4" />
        {s.ч.map((q, i) => (
          <circle key={i} cx={q.x} cy={q.y} r={r} className={q.вид ? 'fill-heavy' : 'fill-el'} />
        ))}
      </svg>

      <div className="lab-readout">
        <span>перемешано: <b>{Math.round(м * 100)} %</b></span>
        <span>
          прошло условно: {ru(s.время, 1)} {ВРЕМЯ[тело].единица}
        </span>
        {тело === 'твёрдое тело' && t > 20 && <span>при {t} °C — в {ru(solidHopRatio(t), 0)} раз быстрее, чем при 20 °C</span>}
      </div>

      <Choice options={(['газ', 'жидкость', 'твёрдое тело'] as const).map((v) => ({ value: v, label: v }))} value={тело} onChange={сменить} />
      <Slider label="Температура" value={t} min={0} max={400} step={10} unit="°C" onChange={setT} />
      <button className="btn" onClick={() => setS({ ч: старт(тело), время: 0 })}>Начать заново</button>

      <Predict
        question="Где перемешивание идёт быстрее всего?"
        options={['в газе', 'в жидкости', 'в твёрдом теле']}
        right={0}
        revealed={м > 0.6 || s.время > 3}
        resetKey={тело}
        explain="В газе расстояния между молекулами намного больше их размеров — молекула пролетает далеко. В жидкости и твёрдом теле молекулы стоят почти вплотную: в жидкости они меняются местами часто, в твёрдом теле — редко. Скорости молекул при этом одного порядка — сотни метров в секунду."
      />
      <p className="muted small">
        Время в каждом опыте своё: запах расходится за минуты, чернила в воде — за часы, золото со свинцом в тисках за
        пять лет проникают друг в друга примерно на миллиметр. Подними температуру — диффузия ускоряется: молекулы
        движутся быстрее.
      </p>
    </div>
  )
}
